import lodash from 'lodash';
import {
    queryGraphQL,
    mutateGraphQL,
} from 'common/util/query';
import {
    node,
    nodeWithArgs,
    toQuery,
    toMutation,
    inlineFragment,
} from 'common/util/queryHelper';
import { addApplicationMessages } from 'common/actions/applicationMessagesActions';

import {
    sortSelector,
    orderSelector,
    querySelector,
} from 'workflow/selectors/inbox';

export const ACTIONS = {
    LOAD_DOCUMENT_ACTIONS: 'LOAD_DOCUMENT_ACTIONS',
    SDFA_LOAD_CONTEXT_DATA: 'SDFA_LOAD_CONTEXT_DATA',
    SDFA_SUCCESS: 'SDFA_SUCCESS',
    CLEAR_SDFA: 'CLEAR_SDFA',
    SDFA_WRITE_INBOX_ITEM: 'SDFA_WRITE_INBOX_ITEM',
    SDFA_WRITE_SENT_ITEM: 'SDFA_WRITE_SENT_ITEM',
    SDFA_TASK_COMPLETION: 'SDFA_TASK_COMPLETION',
    SDFA_CLEAR_ITEM: 'SDFA_CLEAR_ITEM',
    SDFA_CANCEL_UPDATE: 'SDFA_CANCEL_UPDATE',
    LOAD_BINDER_USERS: 'LOAD_BINDER_USERS',
    LOAD_USERS_FOR_SDFA: 'LOAD_USERS_FOR_SDFA',
    RELOAD_USERS_FOR_SDFA: 'RELOAD_USERS_FOR_SDFA',
    STORE_DOCUMENT_NAME: 'STORE_DOCUMENT_NAME',
};

// Sdfa start form actions

function loadDocumentActionsAction(payload) {
    return {
        type: ACTIONS.LOAD_DOCUMENT_ACTIONS,
        payload,
    };
}

function loadUsersForSDFA(payload) {
    return {
        type: ACTIONS.LOAD_USERS_FOR_SDFA,
        payload,
    };
}

function reloadUsersForSDFA(payload) {
    return {
        type: ACTIONS.RELOAD_USERS_FOR_SDFA,
        payload,
    };
}

function loadBinderUsers(payload) {
    return {
        type: ACTIONS.LOAD_BINDER_USERS,
        payload,
    };
}

function sdfaSuccessAction(payload) {
    return {
        type: ACTIONS.SDFA_SUCCESS,
        payload,
    };
}

export function loadSDFAContextData(payload) {
    return {
        type: ACTIONS.SDFA_LOAD_CONTEXT_DATA,
        payload: {
            binderTitle: payload.binder.title,
            documentName: payload.document.name,
        }
    };
}

export function loadSDFADocumentBinderUserData(binderId, documentId) {
    const query = toQuery([
        node('documentActions', [
            'id',
            'displayName',
        ]),
        nodeWithArgs('binder', {
            id: binderId,
        }, [
            'title',
            node('users', [
                'id',
                'displayName',
                'firstName',
                'lastName',
            ]),
        ]),
        nodeWithArgs('usersForSDFA', {
            binderId: binderId
        }, [
            node('institutionUsers', [
                'id',
                'sub',
                'displayName',
                'firstName',
                'lastName',
                'emailAddress',
            ]),
            node('externalUsers', [
                'id',
                'sub',
                'displayName',
                'firstName',
                'lastName',
                'emailAddress',
            ]),
        ]),
        nodeWithArgs('document', {
            id: documentId,
            binderId,
        }, [
            'name',
        ]),
    ]);

    return dispatch => queryGraphQL(query).then(response => {
        if (response.errors) {
            dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
        } else {
            dispatch(loadBinderUsers(response.data.binder.users));
            dispatch(loadUsersForSDFA(response.data.usersForSDFA));
            dispatch(loadDocumentActionsAction(response.data.documentActions));
            dispatch(loadSDFAContextData(response.data));
        }
    });
}

export function reloadUsers(queryText = '', binderId) {
    const query = toQuery(nodeWithArgs('usersForSDFA', {
        queryText,
        binderId: binderId
    }, [
        node('institutionUsers', [
            'id',
            'sub',
            'displayName',
            'firstName',
            'lastName',
            'emailAddress',
        ]),
        node('externalUsers', [
            'id',
            'sub',
            'displayName',
            'firstName',
            'lastName',
            'emailAddress',
        ]),
    ]),);

    return dispatch => queryGraphQL(query).then(response => {
        if (response.errors) {
            dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
        } else {
            dispatch(reloadUsersForSDFA(response.data.usersForSDFA));
        }
    });
}

export function submitSdfa(sdfaRequest) {
    const {
        actionName,
        recipients,
        richTextDescription,
        documentId,
        binderId,
        studyId,
        title,
        dueDate,
        reminderDate,
        shortDescription,
        dueReminderFrequency,
    } = sdfaRequest;

    const mutation = toMutation(nodeWithArgs('sdfaSubmit', {
        input: {
            actionName,
            recipients,
            richTextDescription,
            documentId,
            binderId,
            studyId,
            title,
            dueDate,
            reminderDate,
            shortDescription,
            dueReminderFrequency,
        },
    }, ['id']));

    return dispatch => {
        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            } else {
                dispatch(addApplicationMessages('success', ['Successfully sent document for action.']));
                dispatch(sdfaSuccessAction(response.data.sdfaSubmit));
            }
        });
    };
}


// SDFA Inbox item page actions

export function loadSdfaInboxItem(id) {
    return (dispatch, getState) => {
        const state = getState();
        const sortBy = sortSelector(state);
        const sortOrder = orderSelector(state);
        const searchText = querySelector(state);
        const query = toQuery(
            nodeWithArgs('taskDetails', {
                taskId: id,
            }, [
                '__typename',
                'id',
                'assignee',
                nodeWithArgs('nextTask', {
                    searchText,
                    sortBy,
                    sortOrder,
                }),
                inlineFragment('SDFATask', [
                    'title',
                    'name',
                    'sentDate',
                    'dueDate',
                    'reminderDate',
                    'actionName',
                    'description',
                    'finished',
                    'deleted',
                    'status',
                    node('signingStatement', [
                        'id',
                        'statement',
                    ]),
                    node('document', [
                        'id',
                        'name',
                        'uploadDate',
                        'institutionId',
                        'documentIdentifier',
                        node('signatures', [
                            'dateSigned',
                        ]),
                        node('uploadUser', [
                            'displayName',
                            'emailAddress',
                        ]),
                    ]),
                ]),
            ]),
        );
        return queryGraphQL(query).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            }
            else {
                const { taskDetails } = response.data;
                const action = writeSdfaInboxItem(taskDetails);
                dispatch(action);
            }
        });
    };
}

export function clearSdfa() {
    return {
        type: ACTIONS.CLEAR_SDFA,
    };
}

function writeSdfaInboxItem(taskDetails, document) {
    return {
        type: ACTIONS.SDFA_WRITE_INBOX_ITEM,
        payload: {
            inboxItem: taskDetails,
            document,
        },
    };
}

export function clearSdfaItem() {
    return {
        type: ACTIONS.SDFA_CLEAR_ITEM,
    };
}

function sdfaTaskCompletion(isSuccess) {
    return {
        type: ACTIONS.SDFA_TASK_COMPLETION,
        payload: { success: isSuccess },
    };
}

export function submitSdfaViewTask(taskId) {
    const mutation = toMutation(
        nodeWithArgs('completeViewSdfaTask', {
            id: taskId,
        }),
    );

    return dispatch => mutateGraphQL(mutation).then(response => {
        if (response.errors) {
            dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            dispatch(sdfaTaskCompletion(false));
        } else {
            dispatch(sdfaTaskCompletion(response.data.completeViewSdfaTask));
            dispatch(addApplicationMessages('success', ['Task completed successfully']));
        }
    });
}

export function submitSdfaSignTask(taskId, signingToken) {
    const mutation = toMutation(
        nodeWithArgs('completeSignSdfaTask', {
            id: taskId,
            signingToken,
        }),
    );

    return dispatch => mutateGraphQL(mutation).then(response => {
        if (response.errors) {
            dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            dispatch(sdfaTaskCompletion(false));
        } else {
            dispatch(sdfaTaskCompletion(response.data.completeSignSdfaTask));
            dispatch(addApplicationMessages('success', ['Task completed successfully']));
        }
    });
}

// Sdfa Sent item page actions

function writeSdfaSentItem(sentItem) {
    return {
        type: ACTIONS.SDFA_WRITE_SENT_ITEM,
        payload: { sentItem },
    };
}

export function loadSdfaSentItem(workflowId, userPermission, binderId) {
    let resolver, queryString;

    if (userPermission) {
        resolver = 'workflowsByProcessVariable';
        queryString = {
            processInstanceId: workflowId,
            processVariableName: 'binderId',
            processVariableValue: binderId,
        };
    } else {
        resolver = 'myWorkflows';
        queryString = {
            id: workflowId,
        };
    }

    const query = toQuery(nodeWithArgs(resolver, queryString, [
        'id',
        'title',
        'name',
        'sentDate',
        'dueDate',
        'author',
        'reminderDate',
        'actionName',
        'status',
        'description',
        node('recipients', [
            'sub',
            'displayName',
            'emailAddress',
        ]),
        node('tasks', [
            'id',
            'assignee',
            'finished',
            'deleted',
        ]),
        node('document', [
            'id',
            'name',
            'uploadDate',
            'institutionId',
            'documentIdentifier',
            node('signatures', [
                'dateSigned',
            ]),
            node('uploadUser', [
                'sub',
                'displayName',
                'emailAddress',
            ]),
            node('documentType', [
                'id',
                'displayName',
            ]),
        ]),
    ]));
    return dispatch => queryGraphQL(query).then(response => {
        if (response.errors) {
            dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
        } else {
            const action = writeSdfaSentItem(lodash.head(userPermission ? response.data.workflowsByProcessVariable : response.data.myWorkflows));
            dispatch(action);
        }
    });
}


function updateCanceledSdfaSentItem(taskId) {
    return {
        type: ACTIONS.SDFA_CANCEL_UPDATE,
        payload: taskId,
    };
}


export function cancelSdfaTask(taskId, multipleTasks = []) {
    return async dispatch => {
        const query = nodeWithArgs('cancelSdfaTask', {
            ...taskId ? { id: taskId } : {},
            multipleTasks
        });

        const response = await mutateGraphQL(toMutation(query));
        if (response.errors) {
            dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
        } else {
            if(taskId)
                dispatch(updateCanceledSdfaSentItem(taskId));
        }
    };
}
